// frontend/src/components/detail/tables/MeasuresTable.jsx
import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Collapse, Typography, Tooltip
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { slugify } from '../../../utils/slug';
import { Link as RouterLink, useLocation } from 'react-router-dom';

/* ---------- helpers ---------- */

function formatDemographics(seg) {
  if (!seg || typeof seg !== 'object') return '—';
  const entries = Object.entries(seg)
    .map(([k, v]) => {
      if (v == null) return null;
      if (Array.isArray(v)) {
        const list = v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
        return `${k}: ${list}`;
      }
      if (typeof v === 'object') {
        const inner = Object.entries(v)
          .map(([ik, iv]) => `${ik}: ${Array.isArray(iv) ? iv.join(', ') : String(iv)}`)
          .join('; ');
        return `${k}: { ${inner} }`;
      }
      return `${k}: ${String(v)}`;
    })
    .filter(Boolean);
  return entries.length ? entries.join(' • ') : '—';
}

function pickJobNode(story, jobName) {
  const arr = Array.isArray(story?.jobs_to_be_done) ? story.jobs_to_be_done : [];
  const target = slugify(jobName || '');
  return arr.find(j => slugify(j?.job_to_be_done_category || '') === target) || null;
}

/**
 * Build per-interview rows for a given measure_of_satisfaction_category within the given job.
 * IMPORTANT: measures live in job.how_they_measure_satisfaction (array of objects).
 * Sub-rows should show the *quote* for that measure entry.
 */
function buildInstancesFromCustomerStories({ customerStories, jobName, measureCategory }) {
  if (!Array.isArray(customerStories) || !jobName || !measureCategory) return [];

  const measureSlug = slugify(measureCategory);
  const rows = [];

  for (const s of customerStories) {
    const job = pickJobNode(s, jobName);
    if (!job) continue;

    const measures = Array.isArray(job.how_they_measure_satisfaction)
      ? job.how_they_measure_satisfaction
      : [];

    // find all measures whose category matches the parent row
    const matches = measures.filter(
      (m) => slugify(m?.measure_of_satisfaction_category || '') === measureSlug
    );

    for (let i = 0; i < matches.length; i++) {
      const m = matches[i];
      rows.push({
        key: `${(s.customer_name || 'Interview')}-${(s.conversation_date || '')}-${measureSlug}-${i}`,
        name: s.customer_name || 'Interview',
        demographics: formatDemographics(s.segmentation_data),
        date: s.conversation_date || '—',
        // show the QUOTE (not the measure text)
        quote: m?.quote || '—',
        href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
        rawStory: s,
        rawJob: job,
        rawMeasure: m
      });
    }
  }

  return rows;
}

/* ---------- component ---------- */

export default function MeasuresTable({ rowsSource = [], customerStories = [], jobName }) {
  const location = useLocation();

  const rows = React.useMemo(() => {
    // Preserve tiles order (already ranked like the dashboard)
    return (rowsSource || []).map((t, i) => {
      const title = t.title || t.measure_of_satisfaction_category || t.category || '—';
      const measureCategory = t.measure_of_satisfaction_category || t.category || title;
      const occurrences = t.occurrences ?? t.frequency ?? t.count ?? t.interviewCount ?? 0;

      const quote =
        typeof t.representativeQuote === 'string' ? t.representativeQuote
        : typeof t.quote === 'string' ? t.quote
        : Array.isArray(t.receipts) && typeof t.receipts[0] === 'string' ? t.receipts[0]
        : '';

      const slug = slugify(measureCategory || title);
      const linkTo = slug ? `/measure/${slug}${location.search || ''}` : null;

      const instances = buildInstancesFromCustomerStories({
        customerStories,
        jobName,
        measureCategory
      });

      return {
        key: `${slug || `row-${i}`}`,
        measureCategory: measureCategory || title,
        occurrences,
        quote,
        linkTo,
        instances
      };
    });
  }, [rowsSource, location.search, customerStories, jobName]);

  const [open, setOpen] = React.useState({});
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  if (!rows.length) return null;

  return (
    <TableContainer component={Box} sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={40} />
            <TableCell>Measure of success</TableCell>
            <TableCell align="right">Occurrences</TableCell>
            <TableCell>Representative quote</TableCell>
            <TableCell align="center" width={56}>Open</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <React.Fragment key={r.key}>
              <TableRow hover>
                <TableCell padding="checkbox">
                  <IconButton size="small" onClick={() => toggle(r.key)} aria-label="Expand row">
                    {open[r.key] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.measureCategory || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">{r.occurrences}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {r.quote || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {r.linkTo ? (
                    <Tooltip title="Open measure details">
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={r.linkTo}
                        aria-label="Open measure details"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="No detail page available">
                      <span>
                        <IconButton size="small" disabled>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>

              {/* Expanded sub-table: interviews that had this measure for the current Job */}
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                  <Collapse in={!!open[r.key]} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2, pl: 6 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Interviews
                      </Typography>

                      {r.instances && r.instances.length > 0 ? (
                        <Table size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Demographics</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell>Quote</TableCell>
                              <TableCell align="center" width={56}>Open</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {r.instances.map((inst) => (
                              <TableRow key={inst.key} hover>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {inst.name || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {inst.demographics || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {inst.date || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {inst.quote || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Open interview">
                                    <span>
                                      <IconButton
                                        size="small"
                                        component="a"
                                        href={inst.href}
                                        target="_blank"
                                        rel="noopener"
                                        aria-label="Open interview"
                                      >
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          No interviews found for this measure.
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
